import { PAGE_FRAME_MAX } from '../lib/layout.js';
import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { AppNavLinks, AppNavUtilities } from './app-nav.js';
import { OutletSwitcher } from './outlet-switcher.js';

/**
 * The product's top bar — brand, outlet, navigation, chrome.
 *
 * WHY THIS EXISTS. A survey found that of 37 dashboard route files, exactly ONE
 * rendered `<AppNav>`. Thirty-six screens were dead ends: you arrived and the
 * only way out was a "Back to queue" link, if that screen happened to include
 * one. That is not a styling problem, it is the absence of a shell — the thing
 * that makes a collection of screens read as one product rather than as a set
 * of pages that happen to share a domain.
 *
 * Mounted once, above the router outlet, so a new screen inherits navigation by
 * existing. The alternative — asking every screen to remember — is exactly the
 * arrangement that produced 36 without it.
 *
 * Sticky rather than fixed: `position: fixed` here would put the bar over page
 * content and re-create the overlap class this session already spent two passes
 * removing (a fixed launcher was measurably covering a button nobody could
 * click). Sticky occupies flow, so nothing can hide underneath it.
 */
export function AppShellBar() {
  const { user, claims, loading } = useAuth();
  const brand = usePlatformBrand();

  // No shell over the sign-in screen or while auth is still resolving: chrome
  // around a login form is noise, and a nav you cannot use is worse than none.
  if (loading || !user) return null;
  const hasTenant = typeof claims?.['defaultTenant'] === 'string';
  const isSuperAdmin =
    typeof claims?.['superadmin'] === 'object' && claims?.['superadmin'] !== null;
  if (!hasTenant && !isSuperAdmin) return null;

  return (
    // `data-app-shell` is the signed-in canary `scripts/check-layout.mjs` looks
    // for. A signed-out route renders a small, tidy login card that passes every
    // layout assertion, so without a marker the sweep would report 36 clean
    // screens it never actually loaded.
    <header data-app-shell style={barStyle}>
      {/* Two tiers, on purpose. The top row is identity and utilities; the row
          below is where you go. It used to be one wrapping row, and at tablet
          width the browser decided the layout: whatever overflowed landed on a
          line of its own — the assistant button, alone, on a band of empty
          space. Neither row wraps now; each is sized to fit. */}
      <div style={topRowStyle}>
        <span style={brandStyle}>
          <span aria-hidden style={markStyle}>
            {brand.shortName?.charAt(0) ?? brand.name.charAt(0)}
          </span>
          <span className="k-shell-brand-name" style={brandNameStyle}>
            {brand.shortName ?? brand.name}
          </span>
        </span>
        <div style={{ minWidth: 0, flexShrink: 1, display: 'flex' }}>
          <OutletSwitcher />
        </div>
        <div style={{ flex: 1, minWidth: 0 }} />
        <AppNavUtilities />
      </div>
      <div style={navTierStyle}>
        <AppNavLinks />
      </div>
    </header>
  );
}

const barStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  background: 'color-mix(in srgb, var(--color-surface) 88%, transparent)',
  backdropFilter: 'saturate(180%) blur(12px)',
  WebkitBackdropFilter: 'saturate(180%) blur(12px)',
  borderBottom: '1px solid var(--color-border)',
};

const topRowStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-2) var(--space-5)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  flexWrap: 'nowrap',
  minWidth: 0,
};

/** Collapses to nothing below the compact breakpoint, where AppNavLinks renders null. */
const navTierStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: '0 var(--space-5) var(--space-2)',
  display: 'flex',
  minWidth: 0,
};

const brandStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5em',
  flexShrink: 0,
};

const markStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 700,
  fontSize: 13,
  letterSpacing: '-0.02em',
  flexShrink: 0,
};

const brandNameStyle: React.CSSProperties = {
  fontWeight: 650,
  fontSize: 'var(--text-meta)',
  letterSpacing: '-0.01em',
  color: 'var(--color-on-surface)',
  whiteSpace: 'nowrap',
};
